import { useEffect } from 'react'
import Joyride, { Step, CallBackProps, STATUS } from 'react-joyride'
import { useSuperAdminStore } from '@/store/superAdminStore'
import { useTourManager } from '@/hooks/useTourManager'
import { TourContent, DEFAULT_TOUR_PROPS } from './tourDesign'
import { useTranslation } from 'react-i18next'

export const AddAdminTour = () => {
  const {
    addAdminTour,
    startAddAdminTour,
    completeAddAdminTour,
  } = useSuperAdminStore()
  const { shouldShowTour, isLoading, updateTourStatus } = useTourManager()
  const { t } = useTranslation('tour')

  // Debug logging
  console.log('AddAdminTour render:', {
    shouldShowTour,
    isLoading,
    hasCompleted: addAdminTour.hasCompleted,
    isActive: addAdminTour.isActive
  })

  useEffect(() => {
    // Only start tour if database says we should show tours AND tour hasn't been completed
    if (!isLoading && shouldShowTour && !addAdminTour.hasCompleted) {
  // console.logAddAdminTour: Auto-starting tour for new user')
      // Scroll to top when tour starts
      window.scrollTo({ top: 0, behavior: 'smooth' })
      startAddAdminTour()
    }
  }, [isLoading, shouldShowTour, addAdminTour.hasCompleted, startAddAdminTour])

  // Define tour steps
  const TOUR_STEPS: Step[] = [
    {
      target: '[data-tour="admin-form-firstname"]',
      content: (
        <TourContent
          title={t('superadmin.addAdminTour.steps.personalInfo.title')}
          content={t('superadmin.addAdminTour.steps.personalInfo.content')}
        />
      ),
      disableBeacon: true,
      placement: 'right',
      spotlightPadding: 20,
      spotlightClicks: false,
      floaterProps: {
        disableAnimation: true
      }
    },
    {
      target: '[data-tour="admin-form-email"]',
      content: (
        <TourContent
          title={t('superadmin.addAdminTour.steps.contactInfo.title')}
          content={t('superadmin.addAdminTour.steps.contactInfo.content')}
        />
      ),
      disableBeacon: true,
      placement: 'right',
      spotlightPadding: 20,
      spotlightClicks: false,
      floaterProps: {
        disableAnimation: true
      }
    },
    {
      target: '[data-tour="admin-form-password"]',
      content: (
        <TourContent
          title={t('superadmin.addAdminTour.steps.setPassword.title')}
          content={t('superadmin.addAdminTour.steps.setPassword.content')}
        />
      ),
      disableBeacon: true,
      placement: 'right',
      spotlightPadding: 20,
      spotlightClicks: false,
      floaterProps: {
        disableAnimation: true
      }
    },
    {
      target: '[data-tour="admin-form-submit"]',
      content: (
        <TourContent
          title={t('superadmin.addAdminTour.steps.createAccount.title')}
          content={t('superadmin.addAdminTour.steps.createAccount.content')}
        />
      ),
      disableBeacon: true,
      placement: 'top',
      spotlightPadding: 20,
      spotlightClicks: false,
      floaterProps: {
        disableAnimation: true
      }
    }
  ]

  // Handle tour callbacks
  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, type, step } = data
    
    // Handle tour completion
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      completeAddAdminTour()
      // Update database to mark tour as completed (userOnboarding = true, hide tours)
      updateTourStatus(true)
    }

    // Ensure proper scroll position for each step
    if (type === 'step:before') {
      const element = document.querySelector(step?.target as string)
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  // Don't render if tour is not active or loading
  if (!addAdminTour.isActive || isLoading) {
    return null
  }

  return (
    <>
      <Joyride
        steps={TOUR_STEPS}
        run={addAdminTour.isActive}
        callback={handleJoyrideCallback}
        {...DEFAULT_TOUR_PROPS}
      />
    </>
  )
} 